import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { isIOS, PDF_MAX_MB } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Search, FileText, FileImage, File as FileIcon, Download,
  Eye, Share2, Loader2, FolderOpen, Award, Image as ImageIcon, Upload, Check, X, UploadCloud, Trash2
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "images", label: "Images" },
  { key: "pdfs", label: "PDFs" },
  { key: "other", label: "Other" }
];

const KIND_BADGE = {
  document: { label: "Document", cls: "bg-sky-100 text-sky-700" },
  upload: { label: "Quiz Image", cls: "bg-purple-100 text-purple-700" },
  certificate: { label: "Certificate", cls: "bg-amber-100 text-amber-700" }
};

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function MediaThumb({ item, blobUrl }) {
  if (item.file_type?.startsWith("image/") && blobUrl) {
    return <img src={blobUrl} alt={item.name} className="w-full h-full object-cover" />;
  }
  const Icon = item.file_type?.startsWith("image/") ? FileImage
    : item.file_type === "application/pdf" ? FileText
    : item.kind === "certificate" ? Award : FileIcon;
  return (
    <div className="w-full h-full flex items-center justify-center bg-slate-100">
      <Icon className="w-10 h-10 text-slate-400" />
    </div>
  );
}

export default function MediaLibrary() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // item | "bulk"
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [shareClass, setShareClass] = useState("");
  const [shareDesc, setShareDesc] = useState("");
  const [sharing, setSharing] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const urlsRef = useRef([]);

  useEffect(() => {
    fetchLibrary();
    const urls = urlsRef.current;
    return () => urls.forEach(u => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const fetchLibrary = async () => {
    try {
      const [lib, cls] = await Promise.all([
        axios.get(`${API}/media-library`, { headers }),
        axios.get(`${API}/classes`, { headers })
      ]);
      setItems(lib.data.items || []);
      setClasses(cls.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load your media library");
    } finally { setLoading(false); }
  };

  const MAX_SIZE = 20 * 1024 * 1024;
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const valid = files.filter(f => {
      const limit = f.type === "application/pdf" ? PDF_MAX_MB * 1024 * 1024 : MAX_SIZE;
      if (f.size > limit) { toast.error(`"${f.name}" is over ${limit / 1024 / 1024} MB — skipped`); return false; }
      return true;
    });
    if (valid.length === 0) return;
    setUploads(valid.map(f => ({ name: f.name, status: "uploading" })));
    await Promise.all(valid.map(async (f, idx) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/upload-file`, fd, { headers });
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, status: "done" } : u));
      } catch (e) {
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, status: "failed" } : u));
      }
    }));
    setUploads(prev => {
      const ok = prev.filter(u => u.status === "done").length;
      const fail = prev.filter(u => u.status === "failed").length;
      if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} added to your library`);
      if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed`);
      return prev;
    });
    setTimeout(() => setUploads([]), 2500);
    fetchLibrary();
  };

  const keyOf = (i) => `${i.kind}:${i.id}`;
  const toggleSelect = (item) => {
    setSelected(prev => {
      const next = { ...prev };
      const k = keyOf(item);
      if (next[k]) delete next[k]; else next[k] = { item_id: item.id, kind: item.kind, name: item.name };
      return next;
    });
  };
  const selCount = Object.keys(selected).length;

  const confirmDelete = async () => {
    setDeleting(true);
    const targets = deleteTarget === "bulk" ? Object.values(selected) : [{ item_id: deleteTarget.id, kind: deleteTarget.kind, name: deleteTarget.name }];
    let ok = 0, fail = 0;
    await Promise.all(targets.map(async (t) => {
      try {
        await axios.delete(`${API}/media-library/${t.kind}/${t.item_id}`, { headers });
        ok += 1;
      } catch (e) { fail += 1; }
    }));
    if (ok) toast.success(ok === 1 ? "File deleted from your library" : `${ok} files deleted from your library`);
    if (fail) toast.error(`${fail} deletion${fail > 1 ? "s" : ""} failed`);
    setSelected({});
    setDeleteTarget(null);
    setDeleting(false);
    fetchLibrary();
  };
  const deleteAffectsClass = deleteTarget === "bulk"
    ? Object.values(selected).some(s => s.kind !== "upload")
    : deleteTarget && deleteTarget.kind !== "upload";

  // Lazy-load image thumbnails
  useEffect(() => {
    items.filter(i => i.file_type?.startsWith("image/")).slice(0, 60).forEach(async (i) => {
      if (thumbs[i.id]) return;
      try {
        const r = await axios.get(`${API}${i.download_url}`, { headers, responseType: "blob" });
        const u = URL.createObjectURL(r.data);
        urlsRef.current.push(u);
        setThumbs(prev => ({ ...prev, [i.id]: u }));
      } catch (e) { /* thumb optional */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const filtered = items.filter(i => {
    if (filter === "images" && !i.file_type?.startsWith("image/")) return false;
    if (filter === "pdfs" && i.file_type !== "application/pdf") return false;
    if (filter === "other" && (i.file_type?.startsWith("image/") || i.file_type === "application/pdf")) return false;
    if (search && !`${i.name} ${i.class_name} ${i.description}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openPreview = async (item) => {
    // iOS: open PDFs in a new tab (opened synchronously so Safari doesn't block it)
    const iosTab = item.file_type === "application/pdf" && isIOS() ? window.open("", "_blank") : null;
    if (!iosTab) { setPreview(item); setPreviewUrl(null); }
    try {
      const r = await axios.get(`${API}${item.download_url}`, { headers, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data], { type: item.file_type || r.data.type }));
      if (iosTab) { iosTab.location.href = u; return; }
      urlsRef.current.push(u);
      setPreviewUrl(u);
    } catch (e) {
      iosTab?.close();
      toast.error("Couldn't load preview");
    }
  };

  const downloadItem = async (item) => {
    try {
      const r = await axios.get(`${API}${item.download_url}`, { headers, responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = item.name || "file";
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const submitShare = async () => {
    if (!shareClass) { toast.error("Pick a class first"); return; }
    setSharing(true);
    try {
      if (bulkMode) {
        const res = await axios.post(`${API}/media-library/share-bulk`, {
          items: Object.values(selected).map(s => ({ item_id: s.item_id, kind: s.kind })),
          class_id: shareClass, description: shareDesc
        }, { headers });
        const cname = classes.find(c => c.class_id === shareClass)?.name || "class";
        toast.success(`Shared ${res.data.shared} file${res.data.shared > 1 ? "s" : ""} to ${cname}`);
        setSelected({});
      } else {
        await axios.post(`${API}/media-library/share`, {
          item_id: shareItem.id, kind: shareItem.kind, class_id: shareClass, description: shareDesc
        }, { headers });
        const cname = classes.find(c => c.class_id === shareClass)?.name || "class";
        toast.success(`Shared "${shareItem.name}" to ${cname}`);
      }
      setShareItem(null); setBulkMode(false); setShareClass(""); setShareDesc("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't share");
    } finally { setSharing(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-slate-950 text-white px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/teacher")} className="text-white hover:bg-slate-800" data-testid="media-library-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <FolderOpen className="w-6 h-6 text-amber-400" />
          <div>
            <h1 className="text-lg font-bold">Media Library</h1>
            <p className="text-[11px] text-slate-400">{items.length} files · browse, preview & reuse everything you've uploaded</p>
          </div>
        </div>
      </div>

      <div
        className="max-w-6xl mx-auto p-4 md:p-6 space-y-4 relative"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); }}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        data-testid="media-dropzone"
      >
        {dragOver && (
          <div className="absolute inset-2 z-20 rounded-2xl border-2 border-dashed border-sky-500 bg-sky-50/90 flex flex-col items-center justify-center pointer-events-none" data-testid="drop-overlay">
            <UploadCloud className="w-10 h-10 text-sky-500 mb-2" />
            <p className="text-sm font-semibold text-sky-700">Drop files to add them to your library</p>
          </div>
        )}
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="media-file-input" />
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search files, classes..." className="pl-9" data-testid="media-search-input" />
          </div>
          <div className="flex gap-1.5 items-center flex-wrap">
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`media-filter-${f.key}`}>
                {f.label}
              </button>
            ))}
            <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-700 text-white" onClick={() => fileInputRef.current?.click()} data-testid="upload-files-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" />Upload Files
            </Button>
          </div>
        </div>

        {uploads.length > 0 && (
          <Card className="p-3" data-testid="upload-progress-card">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Uploading {uploads.length} file{uploads.length > 1 ? "s" : ""}</div>
            <div className="space-y-1.5">
              {uploads.map((u, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`upload-row-${i}`}>
                  {u.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />
                    : u.status === "done" ? <Check className="w-3.5 h-3.5 text-green-600" />
                    : <X className="w-3.5 h-3.5 text-red-500" />}
                  <span className="truncate flex-1">{u.name}</span>
                  <span className={`text-xs ${u.status === "done" ? "text-green-600" : u.status === "failed" ? "text-red-500" : "text-slate-400"}`}>{u.status}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {selCount > 0 && (
          <div className="sticky top-2 z-10 flex items-center gap-2 bg-slate-900 text-white rounded-full px-4 py-2 shadow-lg w-fit" data-testid="bulk-share-bar">
            <span className="text-sm font-semibold">{selCount} selected</span>
            <Button size="sm" className="h-7 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-xs" onClick={() => { setBulkMode(true); setShareClass(""); setShareDesc(""); }} data-testid="bulk-share-btn">
              <Share2 className="w-3 h-3 mr-1" />Share Together
            </Button>
            <Button size="sm" className="h-7 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs" onClick={() => setDeleteTarget("bulk")} data-testid="bulk-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" />Delete
            </Button>
            <button className="text-xs text-slate-300 hover:text-white ml-1" onClick={() => setSelected({})} data-testid="bulk-clear-btn">Clear</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading your library...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="media-empty-state">
            <ImageIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">{items.length === 0 ? "Nothing here yet — drag & drop files anywhere on this page, or use Upload Files." : "No files match your search."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
            {filtered.map(item => {
              const badge = KIND_BADGE[item.kind] || KIND_BADGE.document;
              return (
                <Card key={`${item.kind}-${item.id}`} className="overflow-hidden group" data-testid={`media-card-${item.id}`}>
                  <div className="aspect-[4/3] relative cursor-pointer" onClick={() => openPreview(item)}>
                    <MediaThumb item={item} blobUrl={thumbs[item.id]} />
                    <span className={`absolute top-2 left-2 text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${badge.cls}`}>{badge.label}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(item); }}
                      className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selected[keyOf(item)] ? "bg-sky-500 border-sky-500" : "bg-white/80 border-slate-300 hover:border-sky-400"}`}
                      data-testid={`select-check-${item.id}`}
                      aria-label="Select file"
                    >
                      {selected[keyOf(item)] && <Check className="w-3.5 h-3.5 text-white" />}
                    </button>
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-semibold truncate" title={item.name}>{item.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {fmtSize(item.file_size)}{item.class_name ? ` · ${item.class_name}` : ""}{item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString()}` : ""}
                    </div>
                    <div className="flex gap-1.5 mt-2.5">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex-1" onClick={() => openPreview(item)} data-testid={`preview-btn-${item.id}`}><Eye className="w-3 h-3 mr-1" />Preview</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => downloadItem(item)} data-testid={`download-btn-${item.id}`}><Download className="w-3 h-3" /></Button>
                      <Button size="sm" className="h-7 px-2 text-xs bg-sky-600 hover:bg-sky-500 text-white" onClick={() => { setShareItem(item); setShareClass(""); setShareDesc(""); }} data-testid={`share-btn-${item.id}`}><Share2 className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteTarget(item)} data-testid={`delete-btn-${item.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPreviewUrl(null); } }}>
        <DialogContent className="sm:max-w-3xl" data-testid="media-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{preview?.name}</DialogTitle>
            <DialogDescription>{preview && `${KIND_BADGE[preview.kind]?.label} · ${fmtSize(preview.file_size)}${preview.class_name ? ` · ${preview.class_name}` : ""}`}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto rounded-lg bg-slate-50 flex items-center justify-center min-h-[200px]">
            {!previewUrl ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : preview?.file_type?.startsWith("image/") ? (
              <img src={previewUrl} alt={preview.name} className="max-w-full max-h-[63vh] object-contain" data-testid="preview-image" />
            ) : preview?.file_type === "application/pdf" ? (
              <iframe src={previewUrl} title={preview.name} className="w-full h-[63vh] border-0" data-testid="preview-pdf" />
            ) : (
              <div className="text-center py-10 text-slate-500 text-sm">
                <FileIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No inline preview for this file type.
                <div className="mt-3"><Button size="sm" onClick={() => downloadItem(preview)}><Download className="w-3.5 h-3.5 mr-1.5" />Download</Button></div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="media-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget === "bulk" ? `Delete ${selCount} file${selCount > 1 ? "s" : ""}?` : `Delete "${deleteTarget?.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteAffectsClass
                ? "Documents and certificates will also be removed from their class — students will no longer see them. "
                : ""}
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-500 text-white" data-testid="delete-confirm-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog */}
      <Dialog open={!!shareItem || bulkMode} onOpenChange={(o) => { if (!o) { setShareItem(null); setBulkMode(false); } }}>
        <DialogContent className="sm:max-w-md" data-testid="media-share-dialog">
          <DialogHeader>
            <DialogTitle>{bulkMode ? `Share ${selCount} Files to Class` : "Share to Class"}</DialogTitle>
            <DialogDescription>
              {bulkMode
                ? `Publish ${Object.values(selected).map(s => `"${s.name}"`).slice(0, 3).join(", ")}${selCount > 3 ? ` and ${selCount - 3} more` : ""} as class documents — no re-upload needed.`
                : `Publish "${shareItem?.name}" as a class document — no re-upload needed.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Class *</Label>
              <Select value={shareClass} onValueChange={setShareClass}>
                <SelectTrigger data-testid="share-class-select"><SelectValue placeholder="Choose a class" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.class_id} value={c.class_id} data-testid={`share-class-option-${c.class_id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea value={shareDesc} onChange={e => setShareDesc(e.target.value)} rows={2} placeholder="A note for your students..." data-testid="share-desc-input" />
            </div>
            <Button className="w-full" onClick={submitShare} disabled={sharing} data-testid="share-submit-btn">
              {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}Share to Class
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
